// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { Modal, Flex, Typography, UseShowHide } from '@teamnine/pcj-ui-v2'

import { Template as ITemplate } from '@/services/templates'
import { useErrorHelper } from '@/app/shared/hooks/useErrorHelper'
import { useUpdateTemplateGoldenStatus } from '@/app/Templates/hooks/useUpdateTemplateGoldenStatus'

type UpdateGoldenStatusModalProps = {
  modal: UseShowHide
  template?: ITemplate
  markAsGolden: boolean
}

export default function UpdateGoldenStatusModal({
  modal,
  template,
  markAsGolden,
}: UpdateGoldenStatusModalProps) {
  const { basicErrorHandler, displayGlobalMessage } = useErrorHelper()
  const { mutateAsync: updateTemplateGoldenStatus, isLoading } =
    useUpdateTemplateGoldenStatus()

  const onConfirm = async () => {
    try {
      if (!template?.id) throw new Error('Template ID is missing.')

      await updateTemplateGoldenStatus({
        template,
        golden: markAsGolden,
      })
      displayGlobalMessage({
        heading: `Template successfully ${
          markAsGolden ? 'marked' : 'unmarked'
        } as Golden.`,
        type: 'success',
      })
      modal.hide()
    } catch (err) {
      basicErrorHandler(err)
    }
  }

  const title = markAsGolden ? 'Mark Template Golden' : 'Remove Golden Status'
  const message = markAsGolden
    ? 'Are you sure you want to designate this template as Golden? Golden templates should not be edited or deleted.'
    : 'Are you sure you want to remove the Golden designation from this template, allowing edits and deletion?'

  return (
    <Modal open={modal.visible} onOpenChange={modal.toggle}>
      <Modal.Display aria-label={title}>
        <Modal.Header title={title} />
        <Modal.Content>
          <Flex direction="column" gap={16}>
            <Typography>{message}</Typography>
          </Flex>
        </Modal.Content>
        <Modal.Footer
          footerAction={{
            primaryButton: {
              label: 'Confirm',
              onClick: onConfirm,
              loading: isLoading,
            },
            secondaryButton: {
              label: 'Cancel',
              closeModal: true,
            },
          }}
        />
      </Modal.Display>
    </Modal>
  )
}
